import React from 'react';
import PropTypes from 'prop-types';
import CheckIcon from './CheckIcon';

/**
 * Checkbox component with custom styling and accessibility support.
 * This component displays a visually styled checkbox while keeping the native <input> element hidden.
 *
 * Props:
 * - checked (bool, required): Indicates whether the checkbox is selected.
 * - onChange (function, required): Callback function triggered when the checkbox state changes.
 * - disabled (bool, optional): Disables the checkbox interaction when true.
 */
const Checkbox = ({ checked, onChange, disabled = false, 'aria-label': ariaLabel }) => {
  const handleChange = (event) => {
    onChange(event.target.checked);
  };

  const checkedClasses = checked
    ? 'bg-custom-light-blue border-custom-light-blue'
    : 'bg-transparent border-black';

  const disabledClasses = disabled ? 'opacity-20 cursor-not-allowed' : 'cursor-pointer';

  return (
    <label className={`relative flex items-center ${disabledClasses}`} aria-label={ariaLabel}>
      {/* Hidden Checkbox */}
      <input
        type="checkbox"
        checked={checked}
        onChange={handleChange}
        disabled={disabled}
        className={`absolute w-full h-full opacity-0 ${disabledClasses}`}
      />
      {/* Visible Checkbox */}
      <span
        className={`w-5 h-5 flex justify-center items-center border-2 rounded transition-all ${checkedClasses}`}
      >
        {checked && <CheckIcon />}
      </span>
    </label>
  );
};

Checkbox.propTypes = {
  checked: PropTypes.bool.isRequired,
  onChange: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  'aria-label': PropTypes.string,
};

export default Checkbox;
